// Push Notification Service for Weather Alerts and Event Reminders

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.registration = null;
  }

  // Request notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      localStorage.setItem('notificationPermission', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Check if notifications are supported and enabled
  isSupported() {
    return 'Notification' in window;
  }

  // Get current permission status
  getPermission() {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  // Show browser notification
  async showNotification(title, options = {}) {
    if (!this.isSupported()) {
      console.warn('Notifications not supported');
      return;
    }

    if (this.getPermission() !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      ...options
    };

    try {
      // Use service worker if available
      if (this.registration && this.registration?.showNotification) {
        await this.registration?.showNotification(title, defaultOptions);
      } else {
        // Fallback to browser notification
        new Notification(title, defaultOptions);
      }

      // Store notification in localStorage for NotificationCenter
      this.storeNotification(title, options);
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  // Store notification for in-app notification center
  storeNotification(title, options) {
    const notifications = JSON.parse(localStorage.getItem('notifications') || '[]');
    const newNotification = {
      id: Date.now(),
      title,
      message: options?.body || '',
      type: options?.tag || 'system',
      timestamp: new Date(),
      read: false,
      icon: this.getIconForType(options?.tag),
      priority: options?.priority || 'medium'
    };

    notifications?.unshift(newNotification);
    // Keep only last 50 notifications
    if (notifications?.length > 50) {
      notifications?.splice(50);
    }

    localStorage.setItem('notifications', JSON.stringify(notifications));
    
    // Update notification count
    const unreadCount = notifications?.filter(n => !n?.read)?.length;
    localStorage.setItem('notificationCount', unreadCount?.toString());
    window.dispatchEvent(new Event('storage'));
  }

  // Get icon name based on notification type
  getIconForType(type) {
    const iconMap = {
      weather: 'CloudRain',
      waitlist: 'UserPlus',
      booking: 'CheckCircle',
      reminder: 'Bell',
      system: 'Settings'
    };
    return iconMap?.[type] || 'Bell';
  }

  // Schedule weather alert notification
  scheduleWeatherAlert(eventName, weatherCondition, eventTime) {
    const message = `Hava durumu güncellemesi: ${eventName} için ${weatherCondition}. Etkinlik saati: ${eventTime}`;
    
    this.showNotification('🌤️ Hava Durumu Uyarısı', {
      body: message,
      tag: 'weather',
      priority: 'high',
      data: { type: 'weather', eventName }
    });
  }

  // Schedule event reminder notification
  scheduleEventReminder(eventName, eventTime, hoursBeforeEvent = 24) {
    const eventDate = new Date(eventTime);
    const now = new Date();
    const timeDiff = eventDate - now;
    const hoursUntilEvent = timeDiff / (1000 * 60 * 60);

    if (hoursUntilEvent <= hoursBeforeEvent && hoursUntilEvent > 0) {
      const message = `${eventName} etkinliğiniz ${Math.round(hoursUntilEvent)} saat sonra başlıyor!`;
      
      this.showNotification('📅 Etkinlik Hatırlatması', {
        body: message,
        tag: 'reminder',
        priority: 'high',
        requireInteraction: true,
        data: { type: 'reminder', eventName }
      });
    }
  }

  // Schedule waitlist notification
  notifyWaitlistAvailable(eventName) {
    this.showNotification('🎉 Yer Açıldı!', {
      body: `${eventName} etkinliği için bekleme listesinden bir yer açıldı. Hemen rezervasyon yapın!`,
      tag: 'waitlist',
      priority: 'high',
      requireInteraction: true,
      data: { type: 'waitlist', eventName }
    });
  }

  // Set service worker registration
  setRegistration(registration) {
    this.registration = registration;
  }

  // Check and send event reminders for upcoming events
  checkUpcomingEvents() {
    const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    const tickets = JSON.parse(localStorage.getItem('userTickets') || '[]');
    
    // Check tickets for upcoming events
    tickets?.forEach(ticket => {
      if (ticket?.status === 'upcoming' || ticket?.status === 'active') {
        const eventDate = new Date(ticket.date);
        const now = new Date();
        const hoursUntil = (eventDate - now) / (1000 * 60 * 60);
        
        // Send reminder 24 hours before
        if (hoursUntil <= 24 && hoursUntil > 23) {
          this.scheduleEventReminder(ticket?.eventName, ticket?.date, 24);
        }
        
        // Send reminder 2 hours before
        if (hoursUntil <= 2 && hoursUntil > 1) {
          this.scheduleEventReminder(ticket?.eventName, ticket?.date, 2);
        }
      }
    });
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
